import { flowRight as compose, groupBy, map, sortBy, values } from 'lodash-es';

/** Alias for the pimreader view row type for convenience within this module. */
export type AgendaRow = mumms.IDGMeetingAgendaRow;

export type PatientInMeeting = {
  patientNumber: string;
  patientFirstName: string;
  patientLastName: string;
  /** Display name of the IDG form associated with this patient at this meeting. */
  formName: string;
  /** Primary key of the patientmanifest row; used to fetch the form for printing. */
  patientManifestId: number | null;
};

/** Team label for master grid: "Team - Sub Group" when sub group is present. */
export const formatTeamLabel = ({
  teamName,
  teamSubGroup,
}: IDGMeeting): string =>
  teamSubGroup ? `${teamName} - ${teamSubGroup}` : teamName;

/** Last, First — used by the detail grid name column. */
export const formatPatientName = ({
  patientLastName,
  patientFirstName,
}: PatientInMeeting): string =>
  [patientLastName, patientFirstName].filter(Boolean).join(', ');

export type IDGMeeting = {
  /** idgmeeting_id from the view — uniquely identifies the meeting. */
  meetingKey: number;
  teamName: string;
  teamSubGroup: string;
  fromDate: string;
  toDate: string;
  patients: PatientInMeeting[];
};

/** Stable key for a printable detail row within a meeting. */
export const patientRowKey = (
  meetingKey: number,
  patient: PatientInMeeting,
): string => `${meetingKey}-${patient.patientManifestId}`;

export const isPatientRowPrintable = (patient: PatientInMeeting): boolean =>
  patient.patientManifestId != null;

// ---------------------------------------------------------------------------
// Projections
// ---------------------------------------------------------------------------

const toPatientInMeeting = (row: AgendaRow): PatientInMeeting => ({
  patientNumber: row.patientnumber,
  patientFirstName: row.patientfirstname ?? '',
  patientLastName: row.patientlastname ?? '',
  formName: row.formname ?? '',
  patientManifestId: row.patientmanifest_id,
});

const sortPatients = (patients: PatientInMeeting[]): PatientInMeeting[] =>
  sortBy(patients, [(p) => p.patientLastName, (p) => p.patientFirstName]);

const toMeeting = (rows: AgendaRow[]): IDGMeeting => {
  const first = rows[0];
  return {
    meetingKey: first.idgmeeting_id,
    teamName: first.teamname ?? '',
    teamSubGroup: first.teamsubgroup ?? '',
    fromDate: first.agendastart ?? '',
    toDate: first.agendastop ?? '',
    patients: sortPatients(map(rows, toPatientInMeeting)),
  };
};

// ---------------------------------------------------------------------------
// Pipeline stages
// ---------------------------------------------------------------------------

const groupByMeetingId = (rows: AgendaRow[]): Record<string, AgendaRow[]> =>
  groupBy(rows, (row) => row.idgmeeting_id);

const meetingsFromGroups = (
  groups: Record<string, AgendaRow[]>,
): IDGMeeting[] => map(values(groups), toMeeting);

const newestFirst = (meetings: IDGMeeting[]): IDGMeeting[] =>
  sortBy(meetings, (m) => m.fromDate).reverse();

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Convert a flat list of `idgmeetingagenda_vw` rows into an ordered list of
 * IDG meetings, each containing the patients reviewed at that meeting.
 *
 * Meetings are sorted newest-first; patients within each meeting are sorted
 * by last name then first name.
 */
export const toIDGMeetingList: (rows: AgendaRow[]) => IDGMeeting[] = compose(
  newestFirst,
  meetingsFromGroups,
  groupByMeetingId,
);
